import React from "react";
import { BadgeCheck } from "lucide-react";
import { IRS_EFILE, AUTHORIZED_CLAIM, UNAUTHORIZED_FALLBACK } from "../../data/compliance.js";

// Pill badge for the authorization claim, used in place of a plain eyebrow
// where the credential is the strongest thing we can lead with.
//
// Reads its text from src/data/compliance.js like TrustBanner does — the claim
// string is never duplicated in a component, so turning the authorization on or
// off (or adding the EFIN) happens in exactly one file.
//
// Colour: a solid VERIFICATION GREEN pill (--verify) with white text.
//
// This took three attempts, so the reasoning is worth keeping. The badge sits
// directly above the hero headline, which is near-black ink containing one
// copper word:
//   1. midnight (--accent-fill) — blurred into the near-black headline
//   2. copper (--accent) — collided with the copper accent word "online."
//   3. green (--verify) — 137 degrees of hue from copper and far off the
//      headline's neutral, so it separates from both.
//
// Note the trap: judging this by contrast RATIO is useless here. Copper and
// this green sit within 1.1:1 of each other in luminance yet look nothing
// alike. Hue separation is the measure that matters for "do these two clash",
// and contrast ratio is the measure for "is the label readable". They answer
// different questions.
//
// Measured: white on --verify 5.48:1, clearing AA. Green also happens to be
// the universal convention for a verified credential, which the previous two
// attempts were fighting rather than using.
//
// The label is small letterspaced uppercase — the hardest case to read — so
// don't drop it below ~11px or thin the weight.
const IrsAuthorizedBadge = ({ className = "" }) => {
  const claim = IRS_EFILE.authorized ? AUTHORIZED_CLAIM : UNAUTHORIZED_FALLBACK;

  return (
    <span
      className={[
        "inline-flex items-center gap-2.5 rounded-full py-[11px] pl-4 pr-5",
        "bg-[var(--verify)] text-white",
        "shadow-[0_6px_18px_color-mix(in_srgb,var(--verify)_32%,transparent)]",
        "ring-1 ring-inset ring-white/20",
        className,
      ].join(" ")}
    >
      <BadgeCheck size={21} className="flex-none" aria-hidden="true" />
      <span className="font-disp font-bold uppercase tracking-[.08em] text-[14px]">{claim.headline}</span>
    </span>
  );
};

export default IrsAuthorizedBadge;
